import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  TextField,
  Grid,
  Alert,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import {
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

/**
 * Institute Profile Page (School Admin)
 * Matches the provided UI structure exactly
 */
const InstituteProfile: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [viewOnly, setViewOnly] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [school, setSchool] = useState<any | null>(null);
  const [branches, setBranches] = useState<any[]>([]);
  const [selectedBranchId, setSelectedBranchId] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    // School Fields (for creation/display)
    name: '',
    code: '',
    email: '',
    phone: '',
    billingName: '',
    address: '',
    city: '',
    state: '',
    country: '',
    pinCode: '',
    accountStartYear: new Date().getFullYear(),
    sessionStartMonth: '',

    // Text/Input Fields
    regAffNo: '',
    branchName: '',
    branchCode: '',
    address1: '',
    address2: '',
    district: '',
    pin: '',
    phoneNo: '',
    mobile: '',
    faxNo: '',
    emailAddress: '',
    websiteName: '',
    principalName: '',
    accreditedBy: '',
    scholarNoPrefix: '',
    scholarNoSuffix: '',
    empNoPrefix: '',
    tcBookPages: '',
    backgroundImage: '',
    inputTextFormat: 'Capitalize',

    // Feature/Behavior Checkboxes
    autoEnrollmentNo: false,
    autoAdmissionNumber: false,
    autoScholarNumber: false,
    autoEmployeeNo: false,
    suggestAutoSection: false,
    suggestAutoHouse: false,
    singleLogin: false,
    loginWithSingleDevice: false,
    changeZoomUrl: false,
    liveClassShowOnTime: false,
    liveClassJoinLogOfStudent: false,
    studentAttendanceThroughAssignment: false,
    studentAttendanceThroughLiveClass: false,
    allowToEditEmpAttendanceTime: false,
    sendStudentWelcomeMsgToClassTeacher: false,
    showStudentContactNoInApp: false,
    showTeacherContactNoInApp: false,
    allowScreenshotsApp: false,
    showYoutubeOptionsApp: false,
    viewClassTestResultInMobileAppWithoutPublish: false,
    showExamListResultWise: false,
    hideResultInMobileAppWhenCurrentFeeDue: false,
    autoRegisterForMobileApp: false,
  });

  useEffect(() => {
    fetchProfile();
    fetchBranches();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get('/system-settings/institute-profile');
      const profile = response.data;
      if (profile?.school) {
        setSchool(profile.school);
      }

      setFormData({
        // School Info
        name: profile.school?.name || '',
        code: profile.school?.code || '',
        email: profile.school?.email || '',
        phone: profile.school?.phone || '',
        branchName: profile.school?.branchName || '',
        branchCode: '',
        address: profile.school?.address || '',
        billingName: profile.school?.billingName || '',
        city: profile.school?.city || '',
        state: profile.school?.state || '',
        country: profile.school?.country || '',
        pinCode: profile.school?.postalCode || '',
        accountStartYear: profile.school?.accountStartYear || new Date().getFullYear(),
        sessionStartMonth: profile.sessionStartMonth || 4,
        // Institute Profile
        regAffNo: profile.regAffNo || '',
        address1: profile.address1 || '',
        address2: profile.address2 || '',
        district: profile.district || '',
        pin: profile.pin || '',
        phoneNo: profile.phoneNo || '',
        mobile: profile.mobile || '',
        faxNo: profile.faxNo || '',
        emailAddress: profile.emailAddress || '',
        websiteName: profile.websiteName || '',
        principalName: profile.principalName || '',
        accreditedBy: profile.accreditedBy || '',
        scholarNoPrefix: profile.scholarNoPrefix || '',
        scholarNoSuffix: profile.scholarNoSuffix || '',
        empNoPrefix: profile.empNoPrefix || '',
        tcBookPages: profile.tcBookPages?.toString() || '',
        backgroundImage: profile.backgroundImage || '',
        inputTextFormat: profile.inputTextFormat || 'Capitalize',
        autoEnrollmentNo: profile.autoEnrollmentNo || false,
        autoAdmissionNumber: profile.autoAdmissionNumber || false,
        autoScholarNumber: profile.autoScholarNumber || false,
        autoEmployeeNo: profile.autoEmployeeNo || false,
        suggestAutoSection: profile.suggestAutoSection || false,
        suggestAutoHouse: profile.suggestAutoHouse || false,
        singleLogin: profile.singleLogin || false,
        loginWithSingleDevice: profile.loginWithSingleDevice || false,
        changeZoomUrl: profile.changeZoomUrl || false,
        liveClassShowOnTime: profile.liveClassShowOnTime || false,
        liveClassJoinLogOfStudent: profile.liveClassJoinLogOfStudent || false,
        studentAttendanceThroughAssignment: profile.studentAttendanceThroughAssignment || false,
        studentAttendanceThroughLiveClass: profile.studentAttendanceThroughLiveClass || false,
        allowToEditEmpAttendanceTime: profile.allowToEditEmpAttendanceTime || false,
        sendStudentWelcomeMsgToClassTeacher: profile.sendStudentWelcomeMsgToClassTeacher || false,
        showStudentContactNoInApp: profile.showStudentContactNoInApp || false,
        showTeacherContactNoInApp: profile.showTeacherContactNoInApp || false,
        allowScreenshotsApp: profile.allowScreenshotsApp || false,
        showYoutubeOptionsApp: profile.showYoutubeOptionsApp || false,
        viewClassTestResultInMobileAppWithoutPublish: profile.viewClassTestResultInMobileAppWithoutPublish || false,
        showExamListResultWise: profile.showExamListResultWise || false,
        hideResultInMobileAppWhenCurrentFeeDue: profile.hideResultInMobileAppWhenCurrentFeeDue || false,
        autoRegisterForMobileApp: profile.autoRegisterForMobileApp || false,
      });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch profile');
    } finally {
      setFetching(false);
    }
  };

  const fetchBranches = async () => {
    try {
      const response = await axios.get('/system-settings/branches');
      setBranches(response.data || []);
    } catch (err: any) {
      // silently ignore for now; branches list is secondary
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddBranch = () => {
    setFormData({
      name: school?.name || '',
      code: school?.code || '',
      email: school?.email || '',
      phone: school?.phone || '',
      billingName: school?.billingName || '',
      address: school?.address || '',
      city: school?.city || '',
      state: school?.state || '',
      country: school?.country || '',
      pinCode: school?.postalCode || '',
      accountStartYear: school?.accountStartYear || new Date().getFullYear(),
      sessionStartMonth: school?.sessionStartMonth || 4,

      regAffNo: '',
      branchName: '',
      branchCode: '',
      address1: '',
      address2: '',
      district: '',
      pin: '',
      phoneNo: '',
      mobile: '',
      faxNo: '',
      emailAddress: '',
      websiteName: '',
      principalName: '',
      accreditedBy: '',
      scholarNoPrefix: '',
      scholarNoSuffix: '',
      empNoPrefix: '',
      tcBookPages: '',
      backgroundImage: '',
      inputTextFormat: 'Capitalize',

      autoEnrollmentNo: false,
      autoAdmissionNumber: false,
      autoScholarNumber: false,
      autoEmployeeNo: false,
      suggestAutoSection: false,
      suggestAutoHouse: false,
      singleLogin: false,
      loginWithSingleDevice: false,
      changeZoomUrl: false,
      liveClassShowOnTime: false,
      liveClassJoinLogOfStudent: false,
      studentAttendanceThroughAssignment: false,
      studentAttendanceThroughLiveClass: false,
      allowToEditEmpAttendanceTime: false,
      sendStudentWelcomeMsgToClassTeacher: false,
      showStudentContactNoInApp: false,
      showTeacherContactNoInApp: false,
      allowScreenshotsApp: false,
      showYoutubeOptionsApp: false,
      viewClassTestResultInMobileAppWithoutPublish: false,
      showExamListResultWise: false,
      hideResultInMobileAppWhenCurrentFeeDue: false,
      autoRegisterForMobileApp: false,
    });
    setIsCreating(true);
    setViewOnly(false);
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setLoading(true);

    try {
      const payload = {
        ...formData,
        tcBookPages: formData.tcBookPages ? parseInt(formData.tcBookPages) : null,
        sessionStartMonth: formData.sessionStartMonth ? parseInt(formData.sessionStartMonth.toString()) : 4,
      };

      if (isCreating) {
        // Create new school/branch
        if (!formData.branchCode) {
          setError('Branch Code is required');
          setLoading(false);
          return;
        }
        const schoolData = {
          name: school?.name || formData.name,
          code: formData.branchCode,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          country: formData.country,
          pinCode: formData.pinCode,
          billingName: formData.billingName,
          accountStartYear: formData.accountStartYear,
          sessionStartMonth: payload.sessionStartMonth,
        };

        await axios.post('/system-settings/branches', {
            branchCode: formData.branchCode,
            branchName: formData.branchName,
            billingName: formData.billingName,
          profile: {
            regAffNo: formData.regAffNo,
            address1: formData.address1,
            address2: formData.address2,
            district: formData.district,
            pin: formData.pin,
            phoneNo: formData.phoneNo,
            mobile: formData.mobile,
            faxNo: formData.faxNo,
            emailAddress: formData.emailAddress,
            websiteName: formData.websiteName,
            principalName: formData.principalName,
            accreditedBy: formData.accreditedBy,
            scholarNoPrefix: formData.scholarNoPrefix,
            scholarNoSuffix: formData.scholarNoSuffix,
            empNoPrefix: formData.empNoPrefix,
            tcBookPages: formData.tcBookPages
              ? Number(formData.tcBookPages)
              : undefined,
            backgroundImage: formData.backgroundImage,
            inputTextFormat: formData.inputTextFormat,

            autoEnrollmentNo: formData.autoEnrollmentNo,
            autoAdmissionNumber: formData.autoAdmissionNumber,
            autoScholarNumber: formData.autoScholarNumber,
            autoEmployeeNo: formData.autoEmployeeNo,
            suggestAutoSection: formData.suggestAutoSection,
            suggestAutoHouse: formData.suggestAutoHouse,
            singleLogin: formData.singleLogin,
            loginWithSingleDevice: formData.loginWithSingleDevice,
            changeZoomUrl: formData.changeZoomUrl,
            liveClassShowOnTime: formData.liveClassShowOnTime,
            liveClassJoinLogOfStudent: formData.liveClassJoinLogOfStudent,
            studentAttendanceThroughAssignment: formData.studentAttendanceThroughAssignment,
            studentAttendanceThroughLiveClass: formData.studentAttendanceThroughLiveClass,
            allowToEditEmpAttendanceTime: formData.allowToEditEmpAttendanceTime,
            sendStudentWelcomeMsgToClassTeacher: formData.sendStudentWelcomeMsgToClassTeacher,
            showStudentContactNoInApp: formData.showStudentContactNoInApp,
            showTeacherContactNoInApp: formData.showTeacherContactNoInApp,
            allowScreenshotsApp: formData.allowScreenshotsApp,
            showYoutubeOptionsApp: formData.showYoutubeOptionsApp,
            viewClassTestResultInMobileAppWithoutPublish:
              formData.viewClassTestResultInMobileAppWithoutPublish,
            showExamListResultWise: formData.showExamListResultWise,
            hideResultInMobileAppWhenCurrentFeeDue:
              formData.hideResultInMobileAppWhenCurrentFeeDue,
            autoRegisterForMobileApp: formData.autoRegisterForMobileApp,
          },
        });
        await fetchBranches();
      } else {
        if (selectedBranchId) {
          await axios.patch(`/system-settings/branches/${selectedBranchId}/institute-profile`, payload);
          await fetchBranches();
        } else {
          await axios.patch('/system-settings/institute-profile', payload);
        }
      }

      // Re-fetch profile to ensure UI is in sync with backend
      await fetchProfile();

      setSuccess(true);
      if (isCreating) {
        setShowForm(false);
        setIsCreating(false);
      }
      setSelectedBranchId(null);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await axios.delete('/system-settings/institute-profile');
      // If we delete the current profile, we might need to redirect or refresh
      // Assuming fetchProfile handles "no profile" gracefully or we just clear state
      setSchool(null);
      setFormData({ ...formData, branchName: '' }); // Clear main fields
      setShowForm(false);
      setOpenDeleteDialog(false);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      await fetchProfile();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete profile');
    } finally {
      setLoading(false);
      setOpenDeleteDialog(false);
    }
  };

  if (fetching) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
        {showForm && (
          <Button startIcon={<ArrowBackIcon />} onClick={() => setShowForm(false)}>
            Back to Branches
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(false)}>
          {isCreating ? 'Branch created successfully' : 'Profile saved successfully'}
        </Alert>
      )}

      {!showForm && (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" component="div">
              Branches
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddBranch}
            >
              Add Branch
            </Button>
          </Box>
          <TableContainer sx={{ maxHeight: 440 }}>
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  <TableCell>Branch Name</TableCell>
                  <TableCell>Branch Code</TableCell>
                  <TableCell>Billing Name</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {branches.map((b) => (
                  <TableRow hover role="checkbox" tabIndex={-1} key={b.id}>
                    <TableCell>{b.branchName || b.name || '-'}</TableCell>
                    <TableCell>{b.branchCode || '-'}</TableCell>
                    <TableCell>{b.billingName || '-'}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="View Details">
                        <IconButton
                          color="primary"
                          onClick={async () => {
                            try {
                              const res = await axios.get(`/system-settings/branches/${b.id}/institute-profile`);
                              const profile = res.data;
                              setFormData({
                                name: profile.school?.name || '',
                                code: profile.school?.code || '',
                                email: profile.school?.email || '',
                                phone: profile.school?.phone || '',
                                billingName: profile.school?.billingName || '',
                                address: profile.school?.address || '',
                                city: profile.school?.city || '',
                                state: profile.school?.state || '',
                                country: profile.school?.country || '',
                                pinCode: profile.school?.postalCode || '',
                                accountStartYear: profile.school?.accountStartYear || new Date().getFullYear(),
                                regAffNo: profile.regAffNo || '',
                                branchName: profile.branch?.branchName || '',
                                branchCode: profile.branch?.branchCode || '',
                                address1: profile.address1 || '',
                                address2: profile.address2 || '',
                                district: profile.district || '',
                                pin: profile.pin || '',
                                phoneNo: profile.phoneNo || '',
                                mobile: profile.mobile || '',
                                faxNo: profile.faxNo || '',
                                emailAddress: profile.emailAddress || '',
                                websiteName: profile.websiteName || '',
                                principalName: profile.principalName || '',
                                accreditedBy: profile.accreditedBy || '',
                                scholarNoPrefix: profile.scholarNoPrefix || '',
                                scholarNoSuffix: profile.scholarNoSuffix || '',
                                empNoPrefix: profile.empNoPrefix || '',
                                tcBookPages: profile.tcBookPages?.toString() || '',
                                backgroundImage: profile.backgroundImage || '',
                                inputTextFormat: profile.inputTextFormat || 'Capitalize',
                                sessionStartMonth: profile.sessionStartMonth || 4,
                                autoEnrollmentNo: profile.autoEnrollmentNo || false,
                                autoAdmissionNumber: profile.autoAdmissionNumber || false,
                                autoScholarNumber: profile.autoScholarNumber || false,
                                autoEmployeeNo: profile.autoEmployeeNo || false,
                                suggestAutoSection: profile.suggestAutoSection || false,
                                suggestAutoHouse: profile.suggestAutoHouse || false,
                                singleLogin: profile.singleLogin || false,
                                loginWithSingleDevice: profile.loginWithSingleDevice || false,
                                changeZoomUrl: profile.changeZoomUrl || false,
                                liveClassShowOnTime: profile.liveClassShowOnTime || false,
                                liveClassJoinLogOfStudent: profile.liveClassJoinLogOfStudent || false,
                                studentAttendanceThroughAssignment: profile.studentAttendanceThroughAssignment || false,
                                studentAttendanceThroughLiveClass: profile.studentAttendanceThroughLiveClass || false,
                                allowToEditEmpAttendanceTime: profile.allowToEditEmpAttendanceTime || false,
                                sendStudentWelcomeMsgToClassTeacher: profile.sendStudentWelcomeMsgToClassTeacher || false,
                                showStudentContactNoInApp: profile.showStudentContactNoInApp || false,
                                showTeacherContactNoInApp: profile.showTeacherContactNoInApp || false,
                                allowScreenshotsApp: profile.allowScreenshotsApp || false,
                                showYoutubeOptionsApp: profile.showYoutubeOptionsApp || false,
                                viewClassTestResultInMobileAppWithoutPublish: profile.viewClassTestResultInMobileAppWithoutPublish || false,
                                showExamListResultWise: profile.showExamListResultWise || false,
                                hideResultInMobileAppWhenCurrentFeeDue: profile.hideResultInMobileAppWhenCurrentFeeDue || false,
                                autoRegisterForMobileApp: profile.autoRegisterForMobileApp || false,
                              });
                              setSelectedBranchId(b.id);
                              setViewOnly(true);
                              setIsCreating(false);
                              setShowForm(true);
                            } catch (err: any) {
                              setError(err.response?.data?.message || 'Failed to load branch profile');
                            }
                          }}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Modify Profile">
                        <IconButton
                          color="secondary"
                          onClick={async () => {
                            try {
                              const res = await axios.get(`/system-settings/branches/${b.id}/institute-profile`);
                              const profile = res.data;
                              setFormData({
                                name: profile.school?.name || '',
                                code: profile.school?.code || '',
                                email: profile.school?.email || '',
                                phone: profile.school?.phone || '',
                                pinCode: profile.school?.pinCode || '',
                                billingName: profile.school?.billingName || '',
                                address: profile.school?.address || '',
                                city: profile.school?.city || '',
                                state: profile.school?.state || '',
                                country: profile.school?.country || '',
                                accountStartYear: profile.school?.accountStartYear || new Date().getFullYear(),
                                regAffNo: profile.regAffNo || '',
                                branchName: profile.branch?.branchName || '',
                                branchCode: profile.branch?.branchCode || '',
                                address1: profile.address1 || '',
                                address2: profile.address2 || '',
                                district: profile.district || '',
                                pin: profile.pin || '',
                                phoneNo: profile.phoneNo || '',
                                mobile: profile.mobile || '',
                                faxNo: profile.faxNo || '',
                                emailAddress: profile.emailAddress || '',
                                websiteName: profile.websiteName || '',
                                principalName: profile.principalName || '',
                                accreditedBy: profile.accreditedBy || '',
                                scholarNoPrefix: profile.scholarNoPrefix || '',
                                scholarNoSuffix: profile.scholarNoSuffix || '',
                                empNoPrefix: profile.empNoPrefix || '',
                                tcBookPages: profile.tcBookPages?.toString() || '',
                                backgroundImage: profile.backgroundImage || '',
                                inputTextFormat: profile.inputTextFormat || 'Capitalize',
                                sessionStartMonth: profile.sessionStartMonth || 4,
                                autoEnrollmentNo: profile.autoEnrollmentNo || false,
                                autoAdmissionNumber: profile.autoAdmissionNumber || false,
                                autoScholarNumber: profile.autoScholarNumber || false,
                                autoEmployeeNo: profile.autoEmployeeNo || false,
                                suggestAutoSection: profile.suggestAutoSection || false,
                                suggestAutoHouse: profile.suggestAutoHouse || false,
                                singleLogin: profile.singleLogin || false,
                                loginWithSingleDevice: profile.loginWithSingleDevice || false,
                                changeZoomUrl: profile.changeZoomUrl || false,
                                liveClassShowOnTime: profile.liveClassShowOnTime || false,
                                liveClassJoinLogOfStudent: profile.liveClassJoinLogOfStudent || false,
                                studentAttendanceThroughAssignment: profile.studentAttendanceThroughAssignment || false,
                                studentAttendanceThroughLiveClass: profile.studentAttendanceThroughLiveClass || false,
                                allowToEditEmpAttendanceTime: profile.allowToEditEmpAttendanceTime || false,
                                sendStudentWelcomeMsgToClassTeacher: profile.sendStudentWelcomeMsgToClassTeacher || false,
                                showStudentContactNoInApp: profile.showStudentContactNoInApp || false,
                                showTeacherContactNoInApp: profile.showTeacherContactNoInApp || false,
                                allowScreenshotsApp: profile.allowScreenshotsApp || false,
                                showYoutubeOptionsApp: profile.showYoutubeOptionsApp || false,
                                viewClassTestResultInMobileAppWithoutPublish: profile.viewClassTestResultInMobileAppWithoutPublish || false,
                                showExamListResultWise: profile.showExamListResultWise || false,
                                hideResultInMobileAppWhenCurrentFeeDue: profile.hideResultInMobileAppWhenCurrentFeeDue || false,
                                autoRegisterForMobileApp: profile.autoRegisterForMobileApp || false,
                              });
                              setSelectedBranchId(b.id);
                              setViewOnly(false);
                              setIsCreating(false);
                              setShowForm(true);
                            } catch (err: any) {
                              setError(err.response?.data?.message || 'Failed to load branch profile');
                            }
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Branch">
                        <IconButton
                          color="error"
                          onClick={async () => {
                            const confirmDelete = window.confirm(`Delete this branch?`);
                            if (!confirmDelete) return;
                            try {
                              await axios.delete(`/system-settings/branches/${b.id}`);
                              await fetchBranches();
                            } catch (err: any) {
                              setError(err.response?.data?.message || 'Failed to delete branch');
                            }
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Delete Confirmation Dialog */}
          <Dialog
            open={openDeleteDialog}
            onClose={() => setOpenDeleteDialog(false)}
          >
            <DialogTitle>Delete Institute Profile</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Are you sure you want to delete this institute profile? This action cannot be undone.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenDeleteDialog(false)} disabled={loading}>
                Cancel
              </Button>
              <Button onClick={handleDelete} color="error" autoFocus disabled={loading}>
                Delete
              </Button>
            </DialogActions>
          </Dialog>
        </Paper>
      )}

      {showForm && (
        <Paper sx={{ p: 3 }}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* School Information */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  School Information {viewOnly && '(Read Only)'}
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={isCreating ? 'School Name' : 'School Name'}
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  disabled={loading || viewOnly || isCreating}
                  InputProps={{ readOnly: true }}
                  variant="filled"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={isCreating ? 'School Code (Auto-filled)' : 'School Code'}
                  name="code"
                  value={formData.code}
                  onChange={handleChange}
                  disabled={loading || viewOnly || isCreating}
                  InputProps={{ readOnly: true }}
                  variant="filled"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={loading || viewOnly || isCreating}
                  variant="filled"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  disabled={loading || viewOnly || isCreating}
                  variant="filled"
                />
              </Grid>

              {/* Additional School Fields for Creation */}
              {isCreating && (
                <>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Address"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      disabled={loading || viewOnly || isCreating}
                      variant="filled"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="City"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      disabled={loading || viewOnly || isCreating}
                      variant="filled"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="State"
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      disabled={loading || viewOnly || isCreating}
                      variant="filled"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Country"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      disabled={loading || viewOnly || isCreating}
                      variant="filled"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Pin Code"
                      name="pin"
                      value={formData.pinCode}
                      onChange={handleChange}
                      disabled={loading || viewOnly || isCreating}
                      variant="filled"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Billing Name"
                      name="billingName"
                      value={formData.billingName}
                      onChange={handleChange}
                      disabled={loading || viewOnly || isCreating}
                      variant="filled"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Account Start Year"
                      name="accountStartYear"
                      value={formData.accountStartYear}
                      onChange={handleChange}
                      disabled={loading || viewOnly || isCreating}
                      variant="filled"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Session Start Month"
                      name="sessionStartMonth"
                      value={formData.sessionStartMonth}
                      onChange={handleChange}
                      disabled={loading || viewOnly || isCreating}
                      variant="filled"
                    />
                  </Grid>
                </>
              )}

              {/* Text/Input Fields Section */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Institute Basic Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Branch Name"
                  name="branchName"
                  value={formData.branchName}
                  onChange={handleChange}
                  disabled={loading || viewOnly}

                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Branch Code"
                  name="branchCode"
                  value={formData.branchCode}
                  onChange={handleChange}
                  disabled={loading}

                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Reg./Aff. No"
                  name="regAffNo"
                  value={formData.regAffNo}
                  onChange={handleChange}
                  disabled={loading || viewOnly}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Principal Name"
                  name="principalName"
                  value={formData.principalName}
                  onChange={handleChange}
                  disabled={loading || viewOnly}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address1"
                  name="address1"
                  value={formData.address1}
                  onChange={handleChange}
                  disabled={loading || viewOnly}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address2"
                  name="address2"
                  value={formData.address2}
                  onChange={handleChange}
                  disabled={loading || viewOnly}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="District"
                  name="district"
                  value={formData.district}
                  onChange={handleChange}
                  disabled={loading || viewOnly}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Pin"
                  name="pin"
                  value={formData.pin}
                  onChange={handleChange}
                  disabled={loading || viewOnly}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  select
                  label="Session Start Month"
                  name="sessionStartMonth"
                  value={formData.sessionStartMonth}
                  onChange={handleSelectChange}
                  disabled={loading || viewOnly}
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((month) => (
                    <MenuItem key={month} value={month}>
                      {new Date(2000, month - 1).toLocaleString('default', { month: 'long' })}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Phone No"
                  name="phoneNo"
                  value={formData.phoneNo}
                  onChange={handleChange}
                  disabled={loading || viewOnly}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Mobile"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                  disabled={loading || viewOnly}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Fax No"
                  name="faxNo"
                  value={formData.faxNo}
                  onChange={handleChange}
                  disabled={loading || viewOnly}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="email"
                  label="E-mail Address"
                  name="emailAddress"
                  value={formData.emailAddress}
                  onChange={handleChange}
                  disabled={loading || viewOnly}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Website Name"
                  name="websiteName"
                  value={formData.websiteName}
                  onChange={handleChange}
                  disabled={loading || viewOnly}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Accredited By"
                  name="accreditedBy"
                  value={formData.accreditedBy}
                  onChange={handleChange}
                  disabled={loading || viewOnly}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Scholar No. Prefix"
                  name="scholarNoPrefix"
                  value={formData.scholarNoPrefix}
                  onChange={handleChange}
                  disabled={loading || viewOnly}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Scholar No. Suffix"
                  name="scholarNoSuffix"
                  value={formData.scholarNoSuffix}
                  onChange={handleChange}
                  disabled={loading || viewOnly}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Emp No. Prefix"
                  name="empNoPrefix"
                  value={formData.empNoPrefix}
                  onChange={handleChange}
                  disabled={loading || viewOnly}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  type="number"
                  label="Number of Pages in One TC-Book"
                  name="tcBookPages"
                  value={formData.tcBookPages}
                  onChange={handleChange}
                  disabled={loading || viewOnly}
                  inputProps={{ min: 1 }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Background Image"
                  name="backgroundImage"
                  value={formData.backgroundImage}
                  onChange={handleChange}
                  disabled={loading || viewOnly}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Input Text Format"
                  name="inputTextFormat"
                  value={formData.inputTextFormat}
                  onChange={handleSelectChange}
                  disabled={loading || viewOnly}
                  SelectProps={{ native: true }}
                >
                  <option value="Capitalize">Capitalize</option>
                  <option value="Title Case">Title Case</option>
                  <option value="Upper Case">Upper Case</option>
                </TextField>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                  Feature Settings
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.autoEnrollmentNo}
                          onChange={handleChange}
                          name="autoEnrollmentNo"
                          disabled={loading || viewOnly}
                        />
                      }
                      label="Auto Enrollment No"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.autoAdmissionNumber}
                          onChange={handleChange}
                          name="autoAdmissionNumber"
                          disabled={loading || viewOnly}
                        />
                      }
                      label="Auto Admission No"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.autoScholarNumber}
                          onChange={handleChange}
                          name="autoScholarNumber"
                          disabled={loading || viewOnly}
                        />
                      }
                      label="Auto Scholar No"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.autoEmployeeNo}
                          onChange={handleChange}
                          name="autoEmployeeNo"
                          disabled={loading || viewOnly}
                        />
                      }
                      label="Auto Employee No"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.suggestAutoSection}
                          onChange={handleChange}
                          name="suggestAutoSection"
                          disabled={loading || viewOnly}
                        />
                      }
                      label="Suggest Auto Section"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.suggestAutoHouse}
                          onChange={handleChange}
                          name="suggestAutoHouse"
                          disabled={loading || viewOnly}
                        />
                      }
                      label="Suggest Auto House"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.singleLogin}
                          onChange={handleChange}
                          name="singleLogin"
                          disabled={loading || viewOnly}
                        />
                      }
                      label="Single Login"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.loginWithSingleDevice}
                          onChange={handleChange}
                          name="loginWithSingleDevice"
                          disabled={loading || viewOnly}
                        />
                      }
                      label="Login With Single Device"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.changeZoomUrl}
                          onChange={handleChange}
                          name="changeZoomUrl"
                          disabled={loading || viewOnly}
                        />
                      }
                      label="Change Zoom URL"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.liveClassShowOnTime}
                          onChange={handleChange}
                          name="liveClassShowOnTime"
                          disabled={loading || viewOnly}
                        />
                      }
                      label="Live Class On Time"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.liveClassJoinLogOfStudent}
                          onChange={handleChange}
                          name="liveClassJoinLogOfStudent"
                          disabled={loading || viewOnly}
                        />
                      }
                      label="Log Student Join"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.studentAttendanceThroughAssignment}
                          onChange={handleChange}
                          name="studentAttendanceThroughAssignment"
                          disabled={loading || viewOnly}
                        />
                      }
                      label="Attend. via Assgn"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.studentAttendanceThroughLiveClass}
                          onChange={handleChange}
                          name="studentAttendanceThroughLiveClass"
                          disabled={loading || viewOnly}
                        />
                      }
                      label="Attend. via Live Class"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.allowToEditEmpAttendanceTime}
                          onChange={handleChange}
                          name="allowToEditEmpAttendanceTime"
                          disabled={loading || viewOnly}
                        />
                      }
                      label="Edit Emp Attendance"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.sendStudentWelcomeMsgToClassTeacher}
                          onChange={handleChange}
                          name="sendStudentWelcomeMsgToClassTeacher"
                          disabled={loading || viewOnly}
                        />
                      }
                      label="Welcome Msg to Teacher"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.showStudentContactNoInApp}
                          onChange={handleChange}
                          name="showStudentContactNoInApp"
                          disabled={loading || viewOnly}
                        />
                      }
                      label="Show Student Contact"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.showTeacherContactNoInApp}
                          onChange={handleChange}
                          name="showTeacherContactNoInApp"
                          disabled={loading || viewOnly}
                        />
                      }
                      label="Show Teacher Contact"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.allowScreenshotsApp}
                          onChange={handleChange}
                          name="allowScreenshotsApp"
                          disabled={loading || viewOnly}
                        />
                      }
                      label="Allow Screenshots"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.showYoutubeOptionsApp}
                          onChange={handleChange}
                          name="showYoutubeOptionsApp"
                          disabled={loading || viewOnly}
                        />
                      }
                      label="Show Youtube Options"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.viewClassTestResultInMobileAppWithoutPublish}
                          onChange={handleChange}
                          name="viewClassTestResultInMobileAppWithoutPublish"
                          disabled={loading || viewOnly}
                        />
                      }
                      label="View Test Result (Unpub)"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.showExamListResultWise}
                          onChange={handleChange}
                          name="showExamListResultWise"
                          disabled={loading || viewOnly}
                        />
                      }
                      label="Show Exam List Result Wise"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.hideResultInMobileAppWhenCurrentFeeDue}
                          onChange={handleChange}
                          name="hideResultInMobileAppWhenCurrentFeeDue"
                          disabled={loading || viewOnly}
                        />
                      }
                      label="Hide Result if Fee Due"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.autoRegisterForMobileApp}
                          onChange={handleChange}
                          name="autoRegisterForMobileApp"
                          disabled={loading || viewOnly}
                        />
                      }
                      label="Auto Register App"
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => setShowForm(false)}
                  disabled={loading}
                >
                  Cancel
                </Button>
                {!viewOnly && (
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : (isCreating ? 'Create Branch' : 'Save Configuration')}
                  </Button>
                )}
              </Grid>
            </Grid>
          </form>
        </Paper>
      )}
    </Box>
  );
};

export default InstituteProfile;
